import { useContext, useState } from 'react';
import { useParams } from 'react-router';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useRole } from '@/lib/stores/useRole';

import {
  glacierPolicyApiCreateMidtermTransactionMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type { Policy } from '@/lib/heyapi/types.gen';
import { TransactionType, UserRoleType } from '@/lib/heyapi/types.gen';

import { AgentAuthorityContext } from '../../AgentAuthorityContext';
import { useGlobalLoading } from '../../GlobalLoadingContext';
import { formatErrorMessage } from '../../Header/utils';
import { EffectiveDateInput } from '../components/effectiveDateInput';

export function EndorsementForm({ policy }: { policy: Policy | undefined }) {
  const [newEffectiveDate, setNewEffectiveDate] = useState('');
  const { setGlobalLoading } = useGlobalLoading();
  const { hasExactRole } = useRole();
  const { refetchAgentAuthority } = useContext(AgentAuthorityContext);
  const { id } = useParams();

  const queryClient = useQueryClient();

  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEffectiveDate(e.target.value);
  };

  const { mutate: createMidterm, isPending } = useMutation({
    ...glacierPolicyApiCreateMidtermTransactionMutation(),
    onError: (error) => {
      setGlobalLoading(false);

      toast({
        title: 'Error',
        description: formatErrorMessage(
          error,
          'An error occurred while creating the endorsement transaction'
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      if (hasExactRole(UserRoleType.AGENT)) {
        refetchAgentAuthority?.();
      }
      queryClient.refetchQueries({ queryKey: qk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Loading New Midterm Transaction</code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/policy-details`;
      }, 500);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGlobalLoading(true);
    createMidterm({
      body: {
        effective_date: newEffectiveDate,
        transaction_type: TransactionType.POLICY_ENDORSEMENT,
      },
      path: {
        account_id: id!,
      },
    });
  };

  // Policy dates are already YYYY-MM-DD strings — exactly what the date input
  // wants for min/max. Guard missing values so they fall back to no bound.
  const effectiveDateMinimum =
    policy?.details?.policyEffectiveDate ?? undefined;

  const effectiveDateMaximum =
    policy?.details?.policyExpirationDate ?? undefined;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      <EffectiveDateInput
        value={newEffectiveDate}
        onChange={handleInputChange}
        orientation="horizontal"
        label="Effective date"
        min={effectiveDateMinimum}
        max={effectiveDateMaximum}
        required
        stretch
      />
      <Button type="submit" disabled={isPending}>
        {isPending ? 'Loading...' : 'Confirm'}
      </Button>
    </form>
  );
}
